import { useCallback } from 'react';

import { useBoothUserLogout } from '@auth-v2';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import z from 'zod';

import { CompactClubTable } from '@blocks';
import { constants } from '@core';
import { useTranslate } from '@i18n';
import { useClubCollections, useCreateActivity } from '@queries';
import { Box, Button, Inline, Spinner, Stack, Text, u } from '@ui';

import { LINKS } from '../utils/routes';

const schema = z.object({
    clubId: z.number().int().positive(),
});

type ActivityCreateForm = {
    clubId: number;
};

export const ActivityCreateScreen = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const preselectedClubId = Number(searchParams.get('club-id'));
    const t = useTranslate();

    const { mutate: logout } = useBoothUserLogout();
    const form = useForm<ActivityCreateForm>({
        resolver: zodResolver(schema),
        defaultValues: {
            clubId: preselectedClubId,
        },
    });
    const { mutateAsync: createActivity, isPending } = useCreateActivity();
    const { data: clubCollections, isLoading } = useClubCollections();

    const handleStart = useCallback(
        async ({ clubId }: ActivityCreateForm) => {
            const { id } = await createActivity(clubId);
            if (id !== null) {
                navigate(LINKS.getActivity(id));
            }
        },
        [createActivity, navigate],
    );

    return (
        <FormProvider {...form}>
            <Stack
                position="fixed"
                bg="canvas"
                width="100dvw"
                height="100dvh"
                inset={0}
                zIndex={constants.ZINDEX.MODAL}
            >
                <Inline
                    alignItems="center"
                    justifyContent="space-between"
                    pt={u(4)}
                    pb={u(3)}
                    px={u(3)}
                    borderBottom="subtle"
                >
                    <Box alignItems="center" flex={1} display={{ xs: 'none', md: 'flex' }}>
                        <Text variant="h2">{t('activity_overview.start_new')}</Text>
                    </Box>
                </Inline>

                <Box flex={1} bg="raised" p={u(4)} minHeight={0} overflow="auto">
                    <Controller
                        control={form.control}
                        name="clubId"
                        render={({ field }) => {
                            if (isLoading) {
                                return <Spinner size="m" />;
                            }

                            if (!clubCollections?.length) {
                                return <Text color="danger">{t('activity_wizard.club_load_error')}</Text>;
                            }

                            return (
                                <CompactClubTable
                                    clubCollections={clubCollections}
                                    displaySelectedClubId={field.value}
                                    onClick={field.onChange}
                                />
                            );
                        }}
                    />
                    {form.formState.errors.clubId && (
                        <Text color="danger" role="alert">
                            {t('activity_wizard.select_club_required')}
                        </Text>
                    )}
                </Box>
                <Inline
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap={{ xs: 'wrap', md: 'nowrap' }}
                    p={u(2)}
                >
                    <Box display="flex" alignItems="center" gap={u(2)}>
                        <Button variant="plain" size="medium" onClick={() => logout()}>
                            {t('menu.logout')}
                        </Button>
                    </Box>

                    <Box display="flex" alignItems="center" gap={u(2)}>
                        <Button
                            variant="primary"
                            size="medium"
                            onClick={form.handleSubmit(handleStart)}
                            isDisabled={isPending}
                            isLoading={isPending}
                        >
                            {t('generic_ui.done')}
                        </Button>
                    </Box>
                </Inline>
            </Stack>
        </FormProvider>
    );
};
